import dayjs from 'dayjs';

const FIRST_MAIL_DATE = 'firstMailDate';
const LAST_MAIL_DATE = 'lastMailDate';
const IN_HOME_START_DATE = 'inHomeStartDate';
const IN_HOME_END_DATE = 'inHomeEndDate';

/**
 * @param {{ version: Reseller.Version, field: Exclude<keyof Reseller.Version, 'id' | 'name'>; value: string; isRecalculationEnabled: boolean; inHomeWindowRange: Reseller.InHomeWindowRange }} params - params
 * @returns {{ changes: { [key: typeof field]: string }, isRecalculationEnabled: boolean }} - changes
 */
export function recalculateVersionDates(params) {
  const { version, field, value, isRecalculationEnabled, inHomeWindowRange } = params;

  /** @type {{ [key in typeof field]: string }} */
  const changes = {};
  /** @type {boolean} */
  let newIsRecalculationEnabled = isRecalculationEnabled;

  if (isRecalculationEnabled && field === FIRST_MAIL_DATE) {
    changes[IN_HOME_START_DATE] = dayjs(value).add(inHomeWindowRange.inHomeStart, 'day').format('YYYY-MM-DD');
  }
  if (isRecalculationEnabled && field === LAST_MAIL_DATE) {
    changes[IN_HOME_END_DATE] = dayjs(value).add(inHomeWindowRange.inHomeEnd, 'day').format('YYYY-MM-DD');
  }
  const inHomeStartDiffInDays = dayjs(value).diff(dayjs(version.firstMailDate), 'day');
  if (field === IN_HOME_START_DATE && inHomeStartDiffInDays !== inHomeWindowRange.inHomeStart) {
    newIsRecalculationEnabled = false;
  }
  const inHomeEndDiffInDays = dayjs(value).diff(dayjs(version.lastMailDate), 'day');
  if (field === IN_HOME_END_DATE && inHomeEndDiffInDays !== inHomeWindowRange.inHomeEnd) {
    newIsRecalculationEnabled = false;
  }

  return { changes, isRecalculationEnabled: newIsRecalculationEnabled };
}

/**
 * Returns the name of the field that will be affected when the given field is edited
 * @param {string} field - The field being edited ('firstMailDate' | 'lastMailDate' | 'inHomeStartDate' | 'inHomeEndDate')
 * @param {boolean} isRecalculationEnabled - Whether recalculation is currently enabled
 * @returns {string|null} The name of the affected field, or null if no field is affected
 */
export function getAffectedField(field, isRecalculationEnabled) {
  if (!isRecalculationEnabled) return null;

  if (field === FIRST_MAIL_DATE) return IN_HOME_START_DATE;
  if (field === LAST_MAIL_DATE) return IN_HOME_END_DATE;

  return null;
}

/**
 * @param {Reseller.Version} version - version
 * @returns {DataGridError<Reseller.Version>[]} - error
 */
export function validateVersionDates(version) {
  const { id, firstMailDate, lastMailDate, inHomeStartDate, inHomeEndDate } = version;
  return /** @type {DataGridError<Reseller.Version>[]} */ (
    [
      !firstMailDate && { id, field: FIRST_MAIL_DATE, error: 'First mail date is required' },
      !lastMailDate && { id, field: LAST_MAIL_DATE, error: 'Last mail date is required' },
      !inHomeStartDate && { id, field: IN_HOME_START_DATE, error: 'In-home start date is required' },
      !inHomeEndDate && { id, field: IN_HOME_END_DATE, error: 'In-home end date is required' },
      dayjs(firstMailDate).isAfter(dayjs(lastMailDate)) && {
        id,
        field: LAST_MAIL_DATE,
        error: 'Last mail date must be equal to or later than first mail date'
      },
      dayjs(inHomeStartDate).isBefore(dayjs(firstMailDate)) && {
        id,
        field: IN_HOME_START_DATE,
        error: 'In-home start date must be equal to or later than first mail date'
      },
      dayjs(inHomeEndDate).isBefore(dayjs(inHomeStartDate)) && {
        id,
        field: IN_HOME_END_DATE,
        error: 'In-home end date must be equal to or later than in-home start date'
      },
      dayjs(inHomeEndDate).isBefore(dayjs(lastMailDate)) && {
        id,
        field: IN_HOME_END_DATE,
        error: 'In-home end date must be equal to or later than last mail date'
      }
    ].filter(Boolean)
  );
}
